import { getOrderPatientFullName } from 'src/distribution_platforms/features/utils/patient_utils';
import { SamdOrderDetailFieldsFragment, SpecimenType } from 'src/types';
import { Region } from 'src/types/env';
import { WorkflowType } from 'src/types/workflows';
import {
  NccnRiskCategoryUtils,
  SurgicalMarginUtils,
  TStageUtils,
} from 'src/utils';
import { formatLocaleDate, parseIsoDateString } from 'src/utils/date_parsing';

export interface OrderField {
  label: string;
  value: string | number | null;
}

const getDemoProstateBiopsyFields = (
  order: SamdOrderDetailFieldsFragment,
): OrderField[] => {
  const fullName = getOrderPatientFullName(order);
  const patientDateOfBirth = parseIsoDateString(
    order.patient?.dateOfBirthPhi ?? null,
  );

  return [
    { label: 'Patient Name', value: fullName },
    {
      label: 'Date of Birth',
      value: patientDateOfBirth ? formatLocaleDate(patientDateOfBirth) : null,
    },
    { label: 'Patient ID', value: order.externalId! },
    { label: 'Age at Diagnosis', value: order.patientAge },
    {
      label: 'Primary Gleason Score',
      value: order.primaryGleasonScore,
    },
    {
      label: 'Secondary Gleason Score',
      value: order.secondaryGleasonScore,
    },
    {
      label: 'T-stage',
      value: order.tStage
        ? TStageUtils.getProstateBiopsyTStageName(order.tStage!)
        : null,
    },
  ];
};

export const getOrderFields = (
  order: SamdOrderDetailFieldsFragment,
  workflowType: WorkflowType,
  region: Region,
): OrderField[] => {
  if (workflowType === WorkflowType.SAMD_DEMO) {
    if (order.specimenType === SpecimenType.ProstateBiopsy) {
      return getDemoProstateBiopsyFields(order);
    }
    throw new Error(`Unsupported specimen type: ${order.specimenType}`);
  }

  return getOrderFieldsByRegion(order, region);
};

export const getOrderFieldsByRegion = (
  order: SamdOrderDetailFieldsFragment,
  region: Region,
): OrderField[] => {
  switch (region) {
    case Region.US:
      if (order.specimenType === SpecimenType.ProstateBiopsy) {
        return [
          { label: 'Patient ID', value: order.externalId! },
          { label: 'Age at Diagnosis', value: order.patientAge },
          {
            label: 'Primary Gleason Score',
            value: order.primaryGleasonScore,
          },
          {
            label: 'Secondary Gleason Score',
            value: order.secondaryGleasonScore,
          },
          {
            label: 'T-stage',
            value: order.tStage
              ? TStageUtils.getProstateBiopsyTStageName(order.tStage!)
              : null,
          },
        ];
      }
      if (order.specimenType === SpecimenType.BreastBiopsy) {
        return [
          { label: 'Patient ID', value: order.externalId! },
          { label: 'Age at Diagnosis', value: order.patientAge },
          { label: 'Tumor Size', value: order.tumorSize },
          { label: 'Pathological Nodal Stage', value: order.nStage },
        ];
      } else {
        throw new Error(`Unsupported specimen type: ${order.specimenType}`);
      }
    case Region.UK:
    case Region.EU:
      if (order.specimenType === SpecimenType.ProstateBiopsy) {
        return [
          { label: 'Patient ID', value: order.externalId! },
          { label: 'Age at Diagnosis', value: order.patientAge },
          {
            label: 'Primary Gleason Score',
            value: order.primaryGleasonScore,
          },
          {
            label: 'Secondary Gleason Score',
            value: order.secondaryGleasonScore,
          },
          {
            label: 'T-stage',
            value: order.tStage
              ? TStageUtils.getProstateBiopsyTStageName(order.tStage)
              : null,
          },
          { label: 'Baseline PSA', value: order.psa },
          {
            label: 'NCCN Risk Category',
            value: order.nccnRiskCategory
              ? NccnRiskCategoryUtils.getNccnRiskCategoryName(
                  order.nccnRiskCategory,
                )
              : null,
          },
        ];
      } else if (order.specimenType === SpecimenType.BreastBiopsy) {
        return [
          { label: 'Patient ID', value: order.externalId! },
          { label: 'Age at Diagnosis', value: order.patientAge },
          { label: 'Tumor Size', value: order.tumorSize },
          { label: 'Pathological Nodal Stage', value: order.nStage },
        ];
      } else if (order.specimenType === SpecimenType.ProstatePostRp) {
        return [
          { label: 'Patient ID', value: order.externalId! },
          { label: 'Age at RP', value: order.patientAge },
          { label: 'Primary Gleason Score', value: order.primaryGleasonScore },
          {
            label: 'Secondary Gleason Score',
            value: order.secondaryGleasonScore,
          },
          {
            label: 'Most recent PSA after prostatectomy (ng/mL)',
            value: order.psa,
          },
          {
            label: 'Pathologic T-stage',
            value: order.tStage
              ? TStageUtils.getProstatePostRpTStageName(order.tStage)
              : null,
          },
          {
            label: 'Surgical Margin',
            value: order.surgicalMargin
              ? SurgicalMarginUtils.getSurgicalMarginName(order.surgicalMargin)
              : null,
          },
        ];
      } else {
        throw new Error(`Unsupported specimen type: ${order.specimenType}`);
      }
    default:
      throw new Error(`Unsupported region: ${region}`);
  }
};
